import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  CheckCircle,
  ArrowRight,
  Menu,
  X,
} from 'lucide-react';

/* ── Scroll-triggered fade-up ─────────────────────────────── */
function useFadeUp(threshold = 0.13) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}
const FadeUp: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children, delay = 0, className = '',
}) => {
  const { ref, visible } = useFadeUp();
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(26px)',
      transition: `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`,
    }}>
      {children}
    </div>
  );
};

/* ── Smooth slow scroll helper ────────────────────────────── */
const smoothScrollTo = (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  const target = document.querySelector(id);
  if (!target) return;
  const start = window.scrollY;
  const end = target.getBoundingClientRect().top + window.scrollY - 72; // 72px nav offset
  const duration = 900; // ms — increase for slower scroll
  let startTime: number | null = null;
  const easeInOutCubic = (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const step = (timestamp: number) => {
    if (!startTime) startTime = timestamp;
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    window.scrollTo(0, start + (end - start) * easeInOutCubic(progress));
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

// Configuration object for easy editing of homepage content
const HOMEPAGE_CONFIG = {
  hero: {
    eyebrow: "The Stemmler Group · Proprietary AI Platform",
    title: "Consulting that moves at the speed of",
    titleAccent: "intelligence.",
    description: "Jarvis is TSG's proprietary AI platform — purpose-built for financial services consulting. It compresses weeks of analysis into hours, pressure-tests every recommendation before the client sees it, and produces board-ready deliverables that used to take three analysts three days.",
    ctaPrimary: "See It in Action",
    ctaSecondary: "What it does",
  },
  company: {
    name: "JARVIS",
    tagline: "TSG's AI Platform for Financial Services Consulting",
    logoPath: "/images/jarvis_logo.png"
  },
  footer: {
    enabled: true,
    copyrightYear: new Date().getFullYear()
  }
};

const heroStats = [
  { label: "More opportunities evaluated with the same team", value: "3–5×" },
  { label: "Reduction in manual documentation work", value: "70%" },
  { label: "Research synthesis: days to hours", value: "2 hrs" },
  { label: "Weeks of status reporting compressed to", value: "20 min" },
  { label: "Security questionnaire: G-SIB bank-grade compliance", value: "256Q" },
];

// Client names + the "Deployed at" band were removed from the public landing page (per request).
// Original list kept here (commented) for easy restore:
//   const clients = ["SMBC", "AmEx", "Pacific Life", "Citizens Bank", "TD Bank"];

const problems = [
  {
    num: "01",
    title: "Weeks spent getting up to speed",
    desc: "New engagements mean reading hundreds of documents, conducting dozens of interviews, and weeks of synthesis before a single insight reaches the client. Teams spend more time learning than thinking."
  },
  {
    num: "02",
    title: "Recommendations that aren't pressure-tested",
    desc: "Analysis goes from analyst to partner to client with limited stress-testing. Blind spots survive until the client finds them — in the room, with executives watching."
  },
  {
    num: "03",
    title: "Data that can't be trusted or understood",
    desc: "Clients hand over exports from systems nobody has seen before. No data dictionary, no schema, no context. Teams spend days profiling data that should take minutes."
  },
  {
    num: "04",
    title: "Status reporting that consumes the whole week",
    desc: "Weekly status decks take 3–4 hours per consultant to assemble manually. That's a full day per month per person spent moving information around, not generating insight."
  },
];

const capabilities = [
  {
    icon: "🧠",
    title: "Instant Document Intelligence",
    desc: "Upload any file — reports, contracts, transcripts, regulatory docs, prior work. Query the entire corpus conversationally and get precise, sourced answers in seconds. Stop reading. Start asking.",
    tag: "Knowledge Retrieval"
  },
  {
    icon: "🗺️",
    title: "Tribal Knowledge in Hours",
    desc: "Surface what is buried in organizational documents, scattered across systems, and locked in SME interviews. New team members get up to speed on complex engagements without weeks of immersion.",
    tag: "Situation Assessment"
  },
  {
    icon: "📊",
    title: "Rapid Data Profiling",
    desc: "Drop in any spreadsheet, export, or multi-system data file. Jarvis identifies structure, flags quality issues, surfaces anomalies, reconciles across sources, and explains what the data means — before analysis begins.",
    tag: "Data Intelligence"
  },
  {
    icon: "⚔️",
    title: "Pressure Testing Ideas",
    desc: "Run any recommendation, strategy, or deliverable through the AI Council before the client sees it. Three independent models find the gaps, unsupported assumptions, and hard objections — so you can address them first.",
    tag: "AI Council"
  },
  {
    icon: "🔗",
    title: "Multi-Source Synthesis",
    desc: "Combine dozens of documents, interviews, data files, and prior analyses into a single structured picture. See where findings converge, where they conflict, and what is missing — simultaneously, not sequentially.",
    tag: "Strategic Synthesis"
  },
  {
    icon: "📤",
    title: "Board-Ready Output, Fast",
    desc: "From synthesized findings to branded executive deck, formatted Word document, or live dashboard in a fraction of the time. Analysts review and refine — they no longer build from scratch.",
    tag: "Automated Delivery"
  },
];

const useCaseTabs = [
  { id: "risk", label: "Risk & Compliance" },
  { id: "pe", label: "Private Equity" },
  { id: "data", label: "Data & Analytics" },
  { id: "strategy", label: "Strategy" },
  { id: "internal", label: "Internal Operations" },
];

const useCasePanels: Record<string, Array<{ scenario: string; title: string; bullets: string[]; modules: string }>> = {
  risk: [
    {
      scenario: '"200 processes to risk-assess. 12 weeks to do it."',
      title: "RCSA Acceleration",
      bullets: [
        "AI extracts process steps, identifies risks, and maps controls from uploaded documentation",
        "Risk scores calculated automatically — exceptions flagged for human review only",
        "Branded risk register, heatmap, and board report generated and ready to deliver",
      ],
      modules: "MODULES: RCSA Intelligence · Risk Analyzer · Document Intelligence"
    },
    {
      scenario: '"Our vendor spend data is across 6 systems and nothing ties out."',
      title: "Multi-System Reconciliation",
      bullets: [
        "Upload flat-file exports from Coupa, ServiceNow, Oracle GL, and any other system",
        "Jarvis normalizes vendor names, cost centers, and period codes across all sources",
        "Anomaly report surfaces every exception with dollar exposure — automatically",
      ],
      modules: "MODULES: Data Reconciliation · Program Intelligence · Smart Data Analyst"
    },
    {
      scenario: '"We need SOPs for every critical process before the audit."',
      title: "Process Documentation at Scale",
      bullets: [
        "SME interviews ingested and mapped into L1–L4 process maps with friction scores",
        "SOPs generated automatically from maps — version controlled from the start",
        "Change Detection keeps SOPs synchronized as processes evolve",
      ],
      modules: "MODULES: SOP Builder · Process Maps · Change Detection"
    },
    {
      scenario: '"The auditors want evidence for 80 controls by Friday."',
      title: "Audit Evidence Assembly",
      bullets: [
        "Upload all artifacts — logs, screenshots, policies, attestations, meeting notes",
        "Jarvis maps evidence to control IDs and framework references automatically",
        "Evidence package assembled, indexed, and formatted per auditor requirements",
      ],
      modules: "MODULES: Document Intelligence · Risk Analyzer · Project Management"
    },
  ],
  pe: [
    {
      scenario: '"We see hundreds of deals a year. We can only look deeply at a fraction."',
      title: "Pipeline Screening at Scale",
      bullets: [
        "Upload CIMs, teasers, and management decks for all inbound deals simultaneously",
        "PE Analyst scores each against your investment thesis — surfacing the top opportunities",
        "Analysts spend deep diligence time only on screened-in deals — 3–5× more coverage",
      ],
      modules: "MODULES: PE Analyst · AI Council · Document Intelligence"
    },
    {
      scenario: '"Diligence is done. IC deck is needed by Thursday."',
      title: "Investment Memo Generation",
      bullets: [
        "AI Council synthesizes all diligence findings into structured IC framing",
        "Financial and operational charts generated from underlying data automatically",
        "Monte Carlo provides returns waterfall with P50/P80/P90 scenario ranges",
      ],
      modules: "MODULES: AI Council · Smart Data Analyst · Monte Carlo · PowerPoint Export"
    },
    {
      scenario: '"We just closed. The portfolio company has siloed data and no analytics."',
      title: "Post-Acquisition AI Activation",
      bullets: [
        "Data landscape mapped — what exists, where it lives, what's missing",
        "Strategic roadmap generated: 90-day activation plan + phased multi-year program",
        "Predictive analytics deployed on operational data — from lagging reports to leading signals",
      ],
      modules: "MODULES: Data Reconciliation · Strategic Synthesis · Smart Data Analyst"
    },
    {
      scenario: '"We need to size the EBITDA improvement opportunity before we set the price."',
      title: "Value Creation Planning",
      bullets: [
        "Operations, cost structure, and benchmarks analyzed for improvement levers",
        "Monte Carlo models EBITDA improvement ranges with confidence intervals — not point estimates",
        "Risk Analyzer flags execution risk per lever before capital is committed",
      ],
      modules: "MODULES: PE Analyst · Monte Carlo · Risk Analyzer · PowerPoint Export"
    },
  ],
  data: [
    {
      scenario: '"Client gave us a data dump Friday. Board deck is Monday."',
      title: "Rapid Data Analysis & Dashboards",
      bullets: [
        "Smart Data Analyst identifies key metrics, patterns, and outliers automatically",
        "Charts and dashboard layouts generated and iterated in minutes",
        "Exported as a branded, board-ready PowerPoint in hours — not days",
      ],
      modules: "MODULES: Smart Data Analyst · Client Branding · PowerPoint Export"
    },
    {
      scenario: '"Stockouts on our best SKUs, dead inventory on the slow ones."',
      title: "Demand Forecasting Intelligence",
      bullets: [
        "Multi-variable forecasting: sales history, seasonality, channel mix, external signals",
        "Monte Carlo scenarios replace single-point forecasts with actionable planning ranges",
        "Live dashboard surfaces forward-looking demand signals by SKU, category, and channel",
      ],
      modules: "MODULES: Smart Data Analyst · Monte Carlo · Program Intelligence"
    },
    {
      scenario: '"Complex transformation program. Leadership wants to know the real timeline."',
      title: "Monte Carlo Program Modeling",
      bullets: [
        "AI intake works even from verbal, ambiguous, or unstructured project descriptions",
        "10,000-iteration simulation with PERT distributions and constraint modeling",
        "Tornado diagram identifies the 3–5 drivers that matter most to the outcome",
      ],
      modules: "MODULES: Monte Carlo Engine · Jarvis Intelligence · PowerPoint Export"
    },
    {
      scenario: '"Too much cash tied up in the wrong inventory at the wrong time."',
      title: "Inventory & Working Capital Optimization",
      bullets: [
        "ABC/XYZ segmentation built automatically from inventory, velocity, and cost data",
        "Carrying cost surfaced by segment — tied directly to financial systems",
        "Quantified working capital improvement per policy option, scenario-modeled",
      ],
      modules: "MODULES: Smart Data Analyst · Data Reconciliation · Risk Analyzer"
    },
  ],
  strategy: [
    {
      scenario: '"We need to understand where we actually are before we can define where we\'re going."',
      title: "Current State Baselining",
      bullets: [
        "All available documentation synthesized into a structured current state picture in days",
        "Friction Heatmap identifies where pain is concentrated across the organization",
        "AI Council produces a multi-perspective assessment — surfacing what one lens misses",
      ],
      modules: "MODULES: Document Intelligence · Friction Heatmap · AI Council"
    },
    {
      scenario: '"Leadership wants a credible ROI model — not a number we reverse-engineered."',
      title: "Business Case & Investment Justification",
      bullets: [
        "Smart Data Analyst baselines current-state cost of inaction with real numbers",
        "Monte Carlo produces probabilistic ROI ranges — not a single number nobody believes",
        "AI Council challenges every benefit assumption before leadership does",
      ],
      modules: "MODULES: Smart Data Analyst · Monte Carlo · AI Council · Strategic Synthesis"
    },
    {
      scenario: '"A new regulation drops. Nobody knows what it means for our controls."',
      title: "Regulatory Change Impact Assessment",
      bullets: [
        "New regulation ingested alongside existing process and control documentation",
        "Change Detection maps every requirement to current controls — in hours, not weeks",
        "Impact assessment and remediation task list generated before competitors finish reading",
      ],
      modules: "MODULES: Change Detection · Document Intelligence · Risk Analyzer"
    },
    {
      scenario: '"We\'re reorganizing and need to define how work should flow going forward."',
      title: "Operating Model Design",
      bullets: [
        "Current RACI, org charts, and process maps synthesized into an ownership picture",
        "AI Council generates alternative operating model designs with explicit tradeoffs",
        "Updated SOPs generated for the new model — AI-native from the start",
      ],
      modules: "MODULES: Process Intelligence · AI Council · SOP Builder · Strategic Synthesis"
    },
  ],
  internal: [
    {
      scenario: '"Client meeting next week. Need a compelling proposal fast."',
      title: "AI-Assisted Proposal Creation",
      bullets: [
        "Call notes and client context synthesized into a structured proposal immediately",
        "AI Council pressure-tests the approach from multiple perspectives",
        "Branded deck assembled and ready — total time: half a day, not three days",
      ],
      modules: "MODULES: Strategic Synthesis · AI Council · Client Branding · PowerPoint Export"
    },
    {
      scenario: '"Weekly status reports are eating 4 hours per consultant."',
      title: "Automated Status Reporting",
      bullets: [
        "Milestone completions logged in Jarvis throughout the week",
        "AI Council drafts the executive status update — RAG indicators applied per workstream",
        "Consultant reviews and sends in 20 minutes — not assembled from scratch over hours",
      ],
      modules: "MODULES: Project Management · AI Council · OKR Dashboard"
    },
    {
      scenario: '"SOW is signed. Team needs to be aligned and moving in 24 hours."',
      title: "Instant Project Activation",
      bullets: [
        "Tasks extracted from the signed SOW automatically — no manual re-entry",
        "OKR dashboard configured with milestones mapped to the agreed timeline",
        "Shared client view active from day one — no separate status document to maintain",
      ],
      modules: "MODULES: Project Management · Document Task Extraction · OKR Dashboard"
    },
    {
      scenario: '"Engagement is ending. We don\'t want to re-learn this next time."',
      title: "Knowledge Capture & Institutional Learning",
      bullets: [
        "Methodologies and reusable frameworks extracted from all engagement deliverables",
        "SOP Builder formalizes repeatable components as internal TSG assets",
        "Future teams can Ask Jarvis and surface relevant prior work instantly",
      ],
      modules: "MODULES: Document Intelligence · SOP Builder · Ask Jarvis"
    },
  ],
};

const outcomes = [
  {
    before: "40 client documents, new engagement, first client meeting Monday — weeks of reading and synthesis before a single insight is ready",
    after: "Full corpus queried conversationally, key findings brief assembled, terminology map generated",
    stat: "2 hrs",
    statLabel: "not 2 weeks"
  },
  {
    before: "Analysis is done, recommendation is ready — but gaps and objections survive until the client finds them in the room",
    after: "AI Council runs 3 independent reviews — Risk Mitigator, CFO lens, Devil's Advocate — every hole found internally first",
    stat: "100%",
    statLabel: "pressure-tested before delivery"
  },
  {
    before: "Board deck build takes 3 days per analyst — charting, formatting, writing, and assembling from scratch every time",
    after: "Charts generated, deck assembled, client branding applied — consultants review and refine rather than build",
    stat: "4 hrs",
    statLabel: "not 3 days"
  },
  {
    before: "SOW signed, but project setup — task creation, OKR mapping, client onboarding — consumes the first full day",
    after: "SOW uploaded, tasks extracted automatically, OKR dashboard live, shared client view active — team is moving",
    stat: "< 2 hrs",
    statLabel: "from SOW to team aligned"
  },
];

const modules = [
  { icon: "📋", name: "Project Management Hub", desc: "AI task extraction, OKR tracking, milestone dashboards, multi-user collaboration" },
  { icon: "📊", name: "Smart Data Analyst", desc: "Automated data profiling, chart generation, dashboard assembly, anomaly detection" },
  { icon: "🔗", name: "Data Reconciliation", desc: "Multi-system normalization, cross-source matching, exception reporting with dollar exposure" },
  { icon: "📉", name: "Monte Carlo Engine", desc: "AI-guided simulation for any project type — from structured data to verbal estimates" },
  { icon: "🤖", name: "AI Council", desc: "Multi-model analysis with challenger personas, dissent detection, and synthesized output" },
  { icon: "🏛️", name: "Strategic Synthesis", desc: "Complex analysis distilled into structured recommendations — across any domain" },
  { icon: "🗺️", name: "SOP Builder & Process Maps", desc: "BPMN process maps, friction heatmaps, SOP generation, and version-controlled change detection" },
  { icon: "⚠️", name: "RCSA & Risk Intelligence", desc: "Risk identification, control mapping, residual scoring, and reporting dashboards" },
  { icon: "🔄", name: "Migration Architect", desc: "App rationalization scoring, dependency mapping, cutover readiness, runbook generation" },
  { icon: "💼", name: "PE Deal Intelligence", desc: "CIM screening, due diligence synthesis, EBITDA lever analysis, IC memo generation" },
  { icon: "📤", name: "PowerPoint & Excel Export", desc: "Client-branded, formatted, print-ready deliverables from every Jarvis module" },
  { icon: "💬", name: "Ask Jarvis", desc: "RAG-powered conversational access to your full project document corpus" },
];

const trustCards = [
  {
    icon: "🔒",
    title: "Tenant Isolation",
    desc: "Every client operates in a fully isolated tenant. Data never crosses organizational boundaries. Your engagement data is yours alone — not used to train models, not accessible to other clients."
  },
  {
    icon: "🛡️",
    title: "G-SIB Bank-Grade Compliance",
    desc: "Jarvis passed a 256-question enterprise security questionnaire for G-SIB bank deployment — covering NIST AI RMF, data residency, PII handling, IAM, CI/CD scanning, and MFA enforcement across all surfaces."
  },
  {
    icon: "🏢",
    title: "Enterprise Deployment",
    desc: "SAML SSO, Azure-native deployment using client's own API keys, full audit trail, and automated tenant isolation verification. Jarvis runs inside your environment — not the other way around."
  },
  {
    icon: "🔍",
    title: "Full Audit Trail",
    desc: "Every AI output is traceable to its source. Confidence scoring and source attribution built into every module. Nothing leaves Jarvis without a documented chain of evidence behind it."
  },
  {
    icon: "🧑‍⚖️",
    title: "Human Oversight by Design",
    desc: "Jarvis is an amplifier, not a replacement. Every recommendation, report, and output is reviewed and validated by TSG consultants before it reaches the client. AI creates the draft. Experts own the decision."
  },
  {
    icon: "📜",
    title: "Framework Alignment",
    desc: "Documented alignment to NIST AI RMF, GDPR data handling principles, and financial services acceptable use standards. Trust Portal documentation available for vendor due diligence submissions."
  },
];

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isDark, setIsDark] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState("risk");

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`min-h-screen overflow-x-hidden transition-colors duration-500 ${isDark ? 'dark bg-[#080F20]' : 'bg-white'}`}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(26px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes drift {
          0%,100% { transform: translate(0,0) rotate(0deg); }
          33%      { transform: translate(14px,-10px) rotate(0.4deg); }
          66%      { transform: translate(-10px,8px) rotate(-0.3deg); }
        }
        .anim-hero-1 { animation: fadeUp 0.7s ease both; }
        .anim-hero-2 { animation: fadeUp 0.7s 0.13s ease both; }
        .anim-hero-3 { animation: fadeUp 0.7s 0.26s ease both; }
        .anim-hero-4 { animation: fadeUp 0.7s 0.4s  ease both; }
        .anim-hero-5 { animation: fadeUp 0.8s 0.52s ease both; }
        .nav-link-ul::after {
          content: '';
          position: absolute;
          bottom: -2px; left: 0;
          width: 0; height: 1.5px;
          background: #6C41A8;
          transition: width 0.25s ease;
        }
        .nav-link-ul:hover::after { width: 100%; }
        .dark .nav-link-ul::after { background: #12C4B8; }
      `}</style>
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isDark
          ? scrolled ? 'bg-[#080F20]/90 backdrop-blur-md border-b border-[rgba(18,196,184,0.12)]' : 'bg-transparent'
          : scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src={HOMEPAGE_CONFIG.company.logoPath}
                alt="Jarvis Logo"
                className="h-8 w-auto object-contain mr-2"
              />
              <span className={`text-xl font-bold ${isDark ? 'text-[#F0F5FC]' : 'text-gray-900'}`}>{HOMEPAGE_CONFIG.company.name}</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#capabilities" onClick={smoothScrollTo('#capabilities')} className={`relative nav-link-ul transition-colors duration-200 text-sm ${isDark ? 'text-[#6A88AA] hover:text-[#F0F5FC]' : 'text-gray-600 hover:text-dusky-purple'}`}>Capabilities</a>
              <a href="#use-cases" onClick={smoothScrollTo('#use-cases')} className={`relative nav-link-ul transition-colors duration-200 text-sm ${isDark ? 'text-[#6A88AA] hover:text-[#F0F5FC]' : 'text-gray-600 hover:text-dusky-purple'}`}>Use Cases</a>
              <a href="#modules" onClick={smoothScrollTo('#modules')} className={`relative nav-link-ul transition-colors duration-200 text-sm ${isDark ? 'text-[#6A88AA] hover:text-[#F0F5FC]' : 'text-gray-600 hover:text-dusky-purple'}`}>Modules</a>
              <a href="#trust" onClick={smoothScrollTo('#trust')} className={`relative nav-link-ul transition-colors duration-200 text-sm ${isDark ? 'text-[#6A88AA] hover:text-[#F0F5FC]' : 'text-gray-600 hover:text-dusky-purple'}`}>Security</a>
              {/* Dark mode toggle */}
              <button
                onClick={() => setIsDark(!isDark)}
                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 text-base ${isDark ? 'bg-[#111E38] text-[#12C4B8] hover:bg-[#162340]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                title={isDark ? 'Switch to Light' : 'Switch to Dark'}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
              {user ? (
                <>
                  <Link to="/app/smart-start" className={`transition text-sm ${isDark ? 'text-[#C8D8EE] hover:text-[#12C4B8]' : 'text-gray-700 hover:text-dusky-purple'}`}>Open Jarvis</Link>
                  <button
                    onClick={handleLogout}
                    className={`transition text-sm ${isDark ? 'text-[#C8D8EE] hover:text-[#12C4B8]' : 'text-gray-700 hover:text-dusky-purple'}`}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className={`transition text-sm ${isDark ? 'text-[#C8D8EE] hover:text-[#12C4B8]' : 'text-gray-700 hover:text-dusky-purple'}`}>Login</Link>
                  <Link
                    to="/signup"
                    className={`px-4 py-2 rounded-lg transition text-sm ${isDark ? 'border border-[#12C4B8] text-[#12C4B8] hover:bg-[#12C4B8] hover:text-[#080F20]' : 'border border-dusky-purple text-dusky-purple hover:bg-dusky-purple hover:text-white'}`}
                  >
                    Request a Demo
                  </Link>
                </>
              )}
            </div>

            {/* Mobile: dark toggle + hamburger */}
            <div className="md:hidden flex items-center gap-2">
              <button
                onClick={() => setIsDark(!isDark)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-all duration-300 ${isDark ? 'bg-[#111E38] text-[#12C4B8]' : 'bg-gray-100 text-gray-600'}`}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={isDark ? 'text-[#C8D8EE]' : 'text-gray-700'}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className={`md:hidden shadow-lg ${isDark ? 'bg-[#0D1830] border-t border-[rgba(18,196,184,0.12)]' : 'bg-white'}`}>
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a href="#capabilities" onClick={smoothScrollTo('#capabilities')} className={`block px-3 py-2 rounded ${isDark ? 'text-[#C8D8EE] hover:bg-[#111E38]' : 'text-gray-700 hover:bg-gray-100'}`}>Capabilities</a>
              <a href="#use-cases" onClick={smoothScrollTo('#use-cases')} className={`block px-3 py-2 rounded ${isDark ? 'text-[#C8D8EE] hover:bg-[#111E38]' : 'text-gray-700 hover:bg-gray-100'}`}>Use Cases</a>
              <a href="#modules" onClick={smoothScrollTo('#modules')} className={`block px-3 py-2 rounded ${isDark ? 'text-[#C8D8EE] hover:bg-[#111E38]' : 'text-gray-700 hover:bg-gray-100'}`}>Modules</a>
              <a href="#trust" onClick={smoothScrollTo('#trust')} className={`block px-3 py-2 rounded ${isDark ? 'text-[#C8D8EE] hover:bg-[#111E38]' : 'text-gray-700 hover:bg-gray-100'}`}>Security</a>
              {user ? (
                <>
                  <Link to="/app/smart-start" className={`block px-3 py-2 rounded ${isDark ? 'text-[#C8D8EE] hover:bg-[#111E38]' : 'text-gray-700 hover:bg-gray-100'}`}>Open Jarvis</Link>
                  <button onClick={handleLogout} className={`block w-full text-left px-3 py-2 rounded ${isDark ? 'text-[#C8D8EE] hover:bg-[#111E38]' : 'text-gray-700 hover:bg-gray-100'}`}>Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" className={`block px-3 py-2 rounded ${isDark ? 'text-[#C8D8EE] hover:bg-[#111E38]' : 'text-gray-700 hover:bg-gray-100'}`}>Login</Link>
                  <Link to="/signup" className={`block px-3 py-2 rounded text-white ${isDark ? 'bg-[#12C4B8] hover:bg-[#0A9790]' : 'bg-dusky-purple'}`}>Request a Demo</Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative pt-20 pb-32 overflow-hidden min-h-screen flex items-center">
        {/* Background gradient blobs */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {isDark ? <>
            <div className="absolute -top-24 -right-24 w-[600px] h-[600px] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(18,196,184,0.10) 0%, transparent 70%)', animation: 'drift 22s ease-in-out infinite' }} />
            <div className="absolute bottom-12 -left-20 w-[500px] h-[500px] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(212,149,42,0.07) 0%, transparent 70%)', animation: 'drift 28s ease-in-out infinite -8s' }} />
            <div className="absolute top-1/2 left-1/3 w-[350px] h-[350px] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(18,196,184,0.06) 0%, transparent 70%)', animation: 'drift 18s ease-in-out infinite -4s' }} />
            <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(18,196,184,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(18,196,184,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)' }} />
          </> : <>
            <div className="absolute -top-24 -right-24 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-dusky-purple/10 to-transparent blur-3xl" style={{ animation: 'drift 22s ease-in-out infinite' }} />
            <div className="absolute bottom-12 -left-20 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-medium-purple/8 to-transparent blur-3xl" style={{ animation: 'drift 28s ease-in-out infinite -8s' }} />
            <div className="absolute top-1/2 left-1/3 w-[350px] h-[350px] rounded-full bg-gradient-to-br from-light-purple/5 to-transparent blur-3xl" style={{ animation: 'drift 18s ease-in-out infinite -4s' }} />
            <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(108,65,168,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(108,65,168,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)' }} />
          </>}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 w-full">
          <div className="grid lg:grid-cols-[1fr_380px] gap-20 items-center">
            {/* Left */}
            <div>
              <div className="flex items-center gap-3 mb-7 anim-hero-1">
                <div className={`w-8 h-px ${isDark ? 'bg-[#12C4B8]' : 'bg-dusky-purple'}`} />
                <span className={`text-xs tracking-widest uppercase font-mono ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>
                  {HOMEPAGE_CONFIG.hero.eyebrow}
                </span>
              </div>

              <h1 className={`text-5xl md:text-7xl font-light mb-8 leading-tight anim-hero-2 ${isDark ? 'text-[#F0F5FC]' : 'text-gray-900'}`}>
                {HOMEPAGE_CONFIG.hero.title}{' '}
                <em className={`not-italic font-light ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>{HOMEPAGE_CONFIG.hero.titleAccent}</em>
              </h1>

              <p className={`text-lg mb-10 max-w-xl leading-relaxed font-light anim-hero-3 ${isDark ? 'text-[#C8D8EE]' : 'text-gray-600'}`}>
                {HOMEPAGE_CONFIG.hero.description}
              </p>

              <div className="flex flex-wrap gap-5 items-center anim-hero-4">
                {user ? (
                  <button
                    onClick={() => navigate('/app/smart-start')}
                    className="bg-gradient-to-r from-dusky-purple to-medium-purple text-white px-8 py-4 rounded-lg text-sm font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center group"
                  >
                    Open Smart Router
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => navigate('/signup')}
                      className={`px-8 py-4 rounded-lg text-sm font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center group ${isDark ? 'bg-[#12C4B8] text-[#080F20] hover:bg-[#0A9790]' : 'bg-gradient-to-r from-dusky-purple to-medium-purple text-white'}`}
                    >
                      {HOMEPAGE_CONFIG.hero.ctaPrimary}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <a href="#capabilities" onClick={smoothScrollTo('#capabilities')} className={`transition text-sm flex items-center gap-2 ${isDark ? 'text-[#6A88AA] hover:text-[#12C4B8]' : 'text-gray-600 hover:text-dusky-purple'}`}>
                      {HOMEPAGE_CONFIG.hero.ctaSecondary}
                      <span className="transition-transform group-hover:translate-x-1">→</span>
                    </a>
                  </>
                )}
              </div>
            </div>

            {/* Right — Stats Card */}
            <div className="hidden lg:block anim-hero-5">
              <div className={`rounded-xl p-8 shadow-2xl relative overflow-hidden transition-all duration-500 group ${isDark ? 'bg-[#111E38] border border-[rgba(18,196,184,0.14)]' : 'bg-white border border-gray-200 hover:shadow-3xl hover:border-dusky-purple/20'}`}
                style={isDark ? { boxShadow: '0 0 0 1px rgba(18,196,184,0.14), 0 8px 40px rgba(18,196,184,0.12), 0 32px 80px rgba(18,196,184,0.06)' } : undefined}
              >
                {/* Top accent line */}
                <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent ${isDark ? 'via-[#12C4B8]' : 'via-dusky-purple'} to-transparent`} />
                <div className={`text-[10px] tracking-widest uppercase font-mono mb-6 ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>
                  Platform Impact · Q1 2026
                </div>
                {heroStats.map((stat, i) => (
                  <div
                    key={i}
                    className={`flex justify-between items-baseline py-3.5 ${
                      i < heroStats.length - 1 ? `border-b ${isDark ? 'border-[rgba(18,196,184,0.10)]' : 'border-gray-100'}` : ''
                    }`}
                  >
                    <div className={`text-xs max-w-[180px] leading-snug ${isDark ? 'text-[#6A88AA]' : 'text-gray-500'}`}>{stat.label}</div>
                    <div className={`text-2xl font-semibold ml-3 whitespace-nowrap ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>{stat.value}</div>
                  </div>
                ))}
                <div className={`mt-5 pt-4 text-[10px] tracking-widest uppercase font-mono ${isDark ? 'border-t border-[rgba(18,196,184,0.10)] text-[#6A88AA]' : 'border-t border-gray-100 text-gray-400'}`}>
                  TSG · DEPLOYED · FINANCIAL SERVICES · ENTERPRISE-GRADE
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CLIENTS band removed (client names taken off the public landing page per request).
             The old "Deployed at: SMBC, AmEx, …" band used an alternating background shade
             (#0D1830 / gray-50) that separated HERO from PROBLEM. With it gone, PROBLEM now takes
             that alternating shade so the page keeps its section-to-section colour rhythm. ── */}

      {/* ── PROBLEM ── */}
      <section className={`py-24 border-t ${isDark ? 'bg-[#0D1830] border-[rgba(18,196,184,0.10)]' : 'bg-gray-50 border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeUp className="mb-16">
            <div className={`text-xs tracking-widest uppercase font-mono mb-4 ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>The Problem We Solve</div>
            <h2 className={`text-4xl md:text-5xl font-light leading-tight ${isDark ? 'text-[#F0F5FC]' : 'text-gray-900'}`}>
              Traditional consulting<br />
              is <em className={`not-italic ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>bottlenecked by time.</em>
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-2 gap-8">
            {problems.map((p, i) => (
              <FadeUp key={p.num} delay={i * 0.1}>
                <div className={`rounded-xl p-8 h-full hover:-translate-y-1 transition-all duration-300 group cursor-default ${isDark ? 'border border-[rgba(18,196,184,0.12)] hover:border-[rgba(18,196,184,0.30)] hover:shadow-[0_8px_30px_rgba(18,196,184,0.08)]' : 'border border-gray-100 hover:shadow-lg hover:border-dusky-purple/20'}`}>
                  <div className={`text-3xl font-light mb-4 font-mono transition-colors duration-300 ${isDark ? 'text-[rgba(18,196,184,0.15)] group-hover:text-[rgba(18,196,184,0.25)]' : 'text-gray-200 group-hover:text-dusky-purple/20'}`}>{p.num}</div>
                  <div className={`text-lg font-semibold mb-3 transition-colors duration-300 ${isDark ? 'text-[#F0F5FC] group-hover:text-[#12C4B8]' : 'text-gray-900 group-hover:text-dusky-purple'}`}>{p.title}</div>
                  <div className={`leading-relaxed text-sm ${isDark ? 'text-[#6A88AA]' : 'text-gray-600'}`}>{p.desc}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── CAPABILITIES ── */}
      <section id="capabilities" className={`py-24 ${isDark ? 'bg-[#080F20]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeUp className="flex flex-col md:flex-row md:justify-between md:items-start mb-16 gap-6">
            <div>
              <div className={`text-xs tracking-widest uppercase font-mono mb-4 ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>Core Capabilities</div>
              <h2 className={`text-4xl md:text-5xl font-light leading-tight ${isDark ? 'text-[#F0F5FC]' : 'text-gray-900'}`}>
                What Jarvis makes<br />
                possible on <em className={`not-italic ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>any</em> engagement.
              </h2>
            </div>
            <p className={`max-w-xs text-sm leading-relaxed md:pt-16 ${isDark ? 'text-[#6A88AA]' : 'text-gray-500'}`}>
              Six foundational capabilities that change what consultants can accomplish — regardless of engagement type, industry, or client complexity.
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div className={`p-8 rounded-xl h-full hover:-translate-y-1.5 transition-all duration-300 group cursor-default ${isDark ? 'bg-[#111E38] border border-[rgba(18,196,184,0.12)] hover:border-[rgba(18,196,184,0.30)] hover:shadow-[0_8px_30px_rgba(18,196,184,0.08)]' : 'bg-white border border-gray-100 hover:shadow-xl hover:border-dusky-purple/20'}`}>
                  <div className="text-3xl mb-5 group-hover:scale-110 transition-transform duration-300 inline-block">{cap.icon}</div>
                  <div className={`font-semibold mb-3 transition-colors duration-300 ${isDark ? 'text-[#F0F5FC] group-hover:text-[#12C4B8]' : 'text-gray-900 group-hover:text-dusky-purple'}`}>{cap.title}</div>
                  <div className={`text-sm leading-relaxed mb-5 ${isDark ? 'text-[#6A88AA]' : 'text-gray-500'}`}>{cap.desc}</div>
                  <span className={`inline-block text-[10px] tracking-widest uppercase font-mono rounded px-2 py-1 transition-colors duration-300 ${isDark ? 'text-[#12C4B8] border border-[rgba(18,196,184,0.25)] group-hover:bg-[rgba(18,196,184,0.06)]' : 'text-dusky-purple border border-dusky-purple/20 group-hover:bg-dusky-purple/5'}`}>
                    {cap.tag}
                  </span>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── USE CASES ── */}
      <section id="use-cases" className={`py-24 ${isDark ? 'bg-[#0D1830]' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeUp className="mb-16">
            <div className={`text-xs tracking-widest uppercase font-mono mb-4 ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>Use Cases</div>
            <h2 className={`text-4xl md:text-5xl font-light leading-tight ${isDark ? 'text-[#F0F5FC]' : 'text-gray-900'}`}>
              Built for the work<br />
              that actually <em className={`not-italic ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>happens.</em>
            </h2>
          </FadeUp>

          {/* Tabs */}
          <FadeUp delay={0.1} className={`flex flex-wrap gap-2 mb-10 pb-4 border-b ${isDark ? 'border-[rgba(18,196,184,0.12)]' : 'border-gray-100'}`}>
            {useCaseTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 ${
                  activeTab === tab.id
                    ? isDark
                      ? 'bg-[#12C4B8] text-[#080F20] shadow-md scale-[1.02]'
                      : 'bg-dusky-purple text-white shadow-md shadow-dusky-purple/20 scale-[1.02]'
                    : isDark
                      ? 'text-[#6A88AA] hover:text-[#F0F5FC] hover:bg-[#111E38]'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </FadeUp>

          {/* Panel — fade on tab switch */}
          <div
            key={activeTab}
            style={{ animation: 'fadeUp 0.3s ease both' }}
            className="grid md:grid-cols-2 gap-6"
          >
            {(useCasePanels[activeTab] || []).map((card, i) => (
              <div key={i} className={`rounded-xl p-7 hover:-translate-y-1 transition-all duration-300 group cursor-default ${isDark ? 'border border-[rgba(18,196,184,0.12)] hover:border-[rgba(18,196,184,0.30)] hover:shadow-[0_8px_30px_rgba(18,196,184,0.08)]' : 'border border-gray-100 hover:shadow-lg hover:border-dusky-purple/20'}`}>
                <div className={`text-xs italic mb-3 ${isDark ? 'text-[#6A88AA]' : 'text-gray-400'}`}>{card.scenario}</div>
                <div className={`font-semibold text-base mb-4 transition-colors duration-300 ${isDark ? 'text-[#F0F5FC] group-hover:text-[#12C4B8]' : 'text-gray-900 group-hover:text-dusky-purple'}`}>{card.title}</div>
                <ul className="space-y-2 mb-5">
                  {card.bullets.map((b, bi) => (
                    <li key={bi} className={`flex items-start gap-2 text-sm ${isDark ? 'text-[#6A88AA]' : 'text-gray-600'}`}>
                      <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 transition-colors duration-300 ${isDark ? 'text-[rgba(18,196,184,0.5)] group-hover:text-[#12C4B8]' : 'text-dusky-purple/60 group-hover:text-dusky-purple/80'}`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className={`text-[10px] tracking-wide uppercase font-mono ${isDark ? 'text-[rgba(18,196,184,0.30)]' : 'text-gray-300'}`}>{card.modules}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUTCOMES ── */}
      <section id="outcomes" className={`py-24 ${isDark ? 'bg-[#080F20]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeUp className="mb-16">
            <div className={`text-xs tracking-widest uppercase font-mono mb-4 ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>Before &amp; After</div>
            <h2 className={`text-4xl md:text-5xl font-light leading-tight ${isDark ? 'text-[#F0F5FC]' : 'text-gray-900'}`}>
              The difference<br />
              is <em className={`not-italic ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>measurable.</em>
            </h2>
          </FadeUp>

          <div className="space-y-4">
            {outcomes.map((row, i) => (
              <FadeUp key={i} delay={i * 0.07}>
                <div className={`grid md:grid-cols-[1fr_40px_1fr_120px] gap-4 items-center rounded-xl p-6 transition-all duration-300 group cursor-default ${isDark ? 'border border-[rgba(18,196,184,0.12)] bg-[#111E38] hover:border-[rgba(18,196,184,0.28)] hover:shadow-[0_4px_20px_rgba(18,196,184,0.07)]' : 'border border-gray-100 bg-white hover:shadow-md hover:border-dusky-purple/20'}`}>
                  <div>
                    <div className={`text-[10px] tracking-widest uppercase font-mono mb-2 ${isDark ? 'text-[#6A88AA]' : 'text-gray-400'}`}>Before Jarvis</div>
                    <div className={`text-sm leading-relaxed ${isDark ? 'text-[#6A88AA]' : 'text-gray-600'}`}>{row.before}</div>
                  </div>
                  <div className={`hidden md:flex items-center justify-center text-xl transition-colors duration-300 ${isDark ? 'text-[rgba(18,196,184,0.20)] group-hover:text-[rgba(18,196,184,0.50)]' : 'text-gray-300 group-hover:text-dusky-purple/40'}`}>→</div>
                  <div>
                    <div className={`text-[10px] tracking-widest uppercase font-mono mb-2 ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>With Jarvis</div>
                    <div className={`text-sm leading-relaxed ${isDark ? 'text-[#C8D8EE]' : 'text-gray-700'}`}>{row.after}</div>
                  </div>
                  <div className="text-center md:text-right">
                    <div className={`text-2xl font-semibold leading-none group-hover:scale-105 transition-transform duration-300 inline-block ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>{row.stat}</div>
                    <div className={`text-xs mt-1 ${isDark ? 'text-[#6A88AA]' : 'text-gray-400'}`}>{row.statLabel}</div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── MODULES ── */}
      <section id="modules" className={`py-24 ${isDark ? 'bg-[#0D1830]' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeUp className="mb-16">
            <div className={`text-xs tracking-widest uppercase font-mono mb-4 ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>Platform Modules</div>
            <h2 className={`text-4xl md:text-5xl font-light leading-tight ${isDark ? 'text-[#F0F5FC]' : 'text-gray-900'}`}>
              One platform.<br />
              Every consulting <em className={`not-italic ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>workflow.</em>
            </h2>
          </FadeUp>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.map((mod, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <div className={`rounded-xl p-5 flex items-start gap-4 h-full hover:-translate-y-1 transition-all duration-300 group cursor-default ${isDark ? 'border border-[rgba(18,196,184,0.12)] hover:border-[rgba(18,196,184,0.30)] hover:shadow-[0_4px_20px_rgba(18,196,184,0.07)]' : 'border border-gray-100 hover:shadow-md hover:border-dusky-purple/20'}`}>
                  <div className="text-2xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">{mod.icon}</div>
                  <div>
                    <div className={`font-semibold text-sm mb-1.5 transition-colors duration-300 ${isDark ? 'text-[#F0F5FC] group-hover:text-[#12C4B8]' : 'text-gray-900 group-hover:text-dusky-purple'}`}>{mod.name}</div>
                    <div className={`text-xs leading-relaxed ${isDark ? 'text-[#6A88AA]' : 'text-gray-500'}`}>{mod.desc}</div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUST / SECURITY ── */}
      <section id="trust" className={`py-24 ${isDark ? 'bg-[#080F20]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeUp className="mb-16">
            <div className={`text-xs tracking-widest uppercase font-mono mb-4 ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>Enterprise Security</div>
            <h2 className={`text-4xl md:text-5xl font-light leading-tight ${isDark ? 'text-[#F0F5FC]' : 'text-gray-900'}`}>
              Built for<br />
              <em className={`not-italic ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>regulated environments.</em>
            </h2>
          </FadeUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trustCards.map((card, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div className={`rounded-xl p-7 h-full hover:-translate-y-1 transition-all duration-300 group cursor-default ${isDark ? 'bg-[#111E38] border border-[rgba(18,196,184,0.12)] hover:border-[rgba(18,196,184,0.30)] hover:shadow-[0_8px_30px_rgba(18,196,184,0.08)]' : 'bg-white border border-gray-100 hover:shadow-lg hover:border-dusky-purple/20'}`}>
                  <div className="text-3xl mb-5 group-hover:scale-110 transition-transform duration-300 inline-block">{card.icon}</div>
                  <div className={`font-semibold mb-3 transition-colors duration-300 ${isDark ? 'text-[#F0F5FC] group-hover:text-[#12C4B8]' : 'text-gray-900 group-hover:text-dusky-purple'}`}>{card.title}</div>
                  <div className={`text-sm leading-relaxed ${isDark ? 'text-[#6A88AA]' : 'text-gray-500'}`}>{card.desc}</div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROOF / NUMBERS ── */}
      <section id="proof" className={`py-20 ${isDark ? 'bg-[#0D1830]' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-16 items-start">
            {/* Quote */}
            <FadeUp>
              <div className={`text-xs tracking-widest uppercase font-mono mb-8 ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>The Numbers</div>
              <blockquote className={`text-2xl font-light leading-relaxed pl-6 italic hover:border-l-4 transition-all duration-300 ${isDark ? 'text-[#C8D8EE] border-l-2 border-[#12C4B8]' : 'text-gray-800 border-l-2 border-dusky-purple'}`}>
                "Jarvis doesn't replace the consultant's judgment. It compresses the time between insight and board-ready deliverable — and pressure-tests every recommendation before it leaves the building."
              </blockquote>
              <div className={`mt-6 pl-6 text-sm font-mono tracking-wide ${isDark ? 'text-[#6A88AA]' : 'text-gray-400'}`}>
                The Stemmler Group · Platform Philosophy
              </div>
            </FadeUp>

            {/* Stats */}
            <div className="space-y-4">
              {[
                { value: "3–5×", label: "More opportunities evaluated with the same analyst team, without adding headcount" },
                { value: "70%", label: "Reduction in manual documentation work across RCSA, SOP, and process mapping engagements" },
                { value: "5+", label: "G-SIB banks + Enterprise financial services clients" },
              ].map((stat, i) => (
                <FadeUp key={i} delay={i * 0.12}>
                  <div className={`rounded-xl p-6 flex items-center gap-6 hover:-translate-y-0.5 transition-all duration-300 group cursor-default ${isDark ? 'border border-[rgba(18,196,184,0.12)] hover:border-[rgba(18,196,184,0.30)] hover:shadow-[0_4px_20px_rgba(18,196,184,0.07)]' : 'border border-gray-100 hover:shadow-md hover:border-dusky-purple/20'}`}>
                    <div className={`text-4xl font-semibold leading-none whitespace-nowrap min-w-[100px] group-hover:scale-105 transition-transform duration-300 ${isDark ? 'text-[#12C4B8]' : 'text-dusky-purple'}`}>{stat.value}</div>
                    <div className={`text-sm leading-relaxed ${isDark ? 'text-[#6A88AA]' : 'text-gray-600'}`}>{stat.label}</div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={`py-28 relative overflow-hidden ${isDark ? 'bg-[#080F20]' : 'bg-gradient-to-r from-dusky-purple via-medium-purple to-light-purple'}`}>
        {isDark && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(18,196,184,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(18,196,184,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(18,196,184,0.10) 0%, transparent 70%)', animation: 'drift 20s ease-in-out infinite' }} />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(212,149,42,0.08) 0%, transparent 70%)', animation: 'drift 25s ease-in-out infinite -10s' }} />
          </div>
        )}
        {!isDark && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-32 -left-32 w-96 h-96 bg-white/5 rounded-full" style={{ animation: 'drift 20s ease-in-out infinite' }} />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-white/5 rounded-full" style={{ animation: 'drift 25s ease-in-out infinite -10s' }} />
          </div>
        )}
        <FadeUp className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className={`text-xs tracking-widest uppercase font-mono mb-6 ${isDark ? 'text-[#12C4B8]' : 'text-white/60'}`}>Get Started</div>
          <h2 className={`text-4xl md:text-5xl font-light mb-5 leading-tight ${isDark ? 'text-[#F0F5FC]' : 'text-white'}`}>
            Ready to move at the<br />
            speed of <em className={`not-italic ${isDark ? 'text-[#12C4B8]' : ''}`}>intelligence?</em>
          </h2>
          <p className={`mb-10 text-base leading-relaxed ${isDark ? 'text-[#6A88AA]' : 'text-white/80'}`}>
            See how Jarvis performs on a real engagement scenario — yours. We'll show you what it does with the documents, data, and problems you're actually working with.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <button
                onClick={() => navigate('/app/smart-start')}
                className={`px-8 py-4 rounded-lg text-sm font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center group ${isDark ? 'bg-[#12C4B8] text-[#080F20] hover:bg-[#0A9790]' : 'bg-white text-dusky-purple'}`}
              >
                Open Smart Router
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/signup')}
                  className={`px-8 py-4 rounded-lg text-sm font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center group ${isDark ? 'bg-[#12C4B8] text-[#080F20] hover:bg-[#0A9790]' : 'bg-white text-dusky-purple'}`}
                >
                  Request a Demo
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <a
                  href="#capabilities"
                  onClick={smoothScrollTo('#capabilities')}
                  className={`px-8 py-4 rounded-lg text-sm font-medium transition-all duration-300 text-center ${isDark ? 'border border-[rgba(18,196,184,0.40)] text-[#12C4B8] hover:border-[#12C4B8] hover:bg-[rgba(18,196,184,0.06)]' : 'border border-white/50 text-white hover:border-white hover:bg-white/10'}`}
                >
                  Explore Capabilities
                </a>
              </>
            )}
          </div>
          <p className={`mt-7 text-xs font-mono tracking-widest uppercase ${isDark ? 'text-[#6A88AA]' : 'text-white/50'}`}>
            Proprietary to The Stemmler Group · Enterprise deployments only
          </p>
        </FadeUp>
      </section>

      {/* ── FOOTER ── */}
      {HOMEPAGE_CONFIG.footer.enabled && (
        <footer className={`py-8 ${isDark ? 'bg-[#050C1A] border-t border-[rgba(18,196,184,0.10)]' : 'bg-gray-900'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <img
                  src={HOMEPAGE_CONFIG.company.logoPath}
                  alt="Jarvis Logo"
                  className="h-6 w-auto object-contain"
                />
                <span className="font-semibold text-white">Jarvis</span>
                <span className={`text-sm ml-1 ${isDark ? 'text-[#6A88AA]' : 'text-gray-500'}`}>by The Stemmler Group</span>
              </div>
              <div className={`text-xs font-mono tracking-wide ${isDark ? 'text-[#6A88AA]' : 'text-gray-500'}`}>
                © {HOMEPAGE_CONFIG.footer.copyrightYear} The Stemmler Group, LLC · All rights reserved · Proprietary &amp; Confidential
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default Landing;
